import { useCallback } from 'react';
import moment from 'moment';
import { CATERING_DELIVERY_MAX_ADVANCE_DAYS, ORDER_TYPES } from '@nandosaus/constants';

// Furthest selectable day (inclusive, days from today). Catering delivery is additionally capped so the
// Uber Direct quote never lands beyond Uber's 30-day pickup limit (see CATERING_DELIVERY_MAX_ADVANCE_DAYS).
export const getCateringMaxDaysFromToday = ({ startsInDays, durationDays }, orderType) => {
  const maxDays = startsInDays + durationDays;
  return orderType === ORDER_TYPES.CATERING_DELIVERY ? Math.min(maxDays, CATERING_DELIVERY_MAX_ADVANCE_DAYS) : maxDays;
};

const computeEffectiveStartsInDays = (restaurant, today, subtotalCents) => {
  if (!restaurant?.cateringWindow) return null;
  const todayDayIndex = today.getDay();
  const config = restaurant.cateringWindow.find(cfg => cfg.dayIndex === todayDayIndex);
  if (!config) return null;

  let startsInDays = config.cateringPickupWindowStartsInDays;

  if (startsInDays === 0 && typeof restaurant.isSameDayCateringPastCutoff === 'function') {
    if (restaurant.isSameDayCateringPastCutoff(today)) {
      startsInDays = 1;
    }
  }

  const standardHours = config.cateringStandardLeadTimeHours;
  if (standardHours != null && subtotalCents != null) {
    const standardDays = Math.ceil(standardHours / 24);
    const maxValue = config.cateringPickupWindowMaxOrderValue ?? restaurant.cateringPickupWindowMaxOrderValueGlobal;
    const subtotalDollars = subtotalCents / 100;
    if (maxValue != null && subtotalDollars >= maxValue && startsInDays < standardDays) {
      startsInDays = standardDays;
    }
  }

  return { startsInDays, durationDays: config.cateringPickupWindowDurationDays };
};

/**
 * Custom hook for validating catering pickup dates
 * Uses restaurant-specific catering window configuration to determine
 * minimum advance notice requirements
 */
export const useCateringDateValidation = () => {
  // Get minimum pickup date based on restaurant's catering configuration for today
  const getMinDate = useCallback((restaurant, subtotalCents) => {
    if (!restaurant?.cateringWindow) {
      const today = new Date();
      const minDate = new Date(today);
      minDate.setDate(today.getDate() + 1);
      minDate.setHours(0, 0, 0, 0);
      return minDate;
    }

    const today = new Date();
    const result = computeEffectiveStartsInDays(restaurant, today, subtotalCents);

    if (!result) {
      const minDate = new Date(today);
      minDate.setDate(today.getDate() + 1);
      minDate.setHours(0, 0, 0, 0);
      return minDate;
    }

    const minDate = new Date(today);
    minDate.setDate(today.getDate() + result.startsInDays);
    minDate.setHours(0, 0, 0, 0);
    return minDate;
  }, []);

  // Date validation function
  const validateDate = useCallback(
    (date, restaurant, subtotalCents, orderType) => {
      if (!date) return false;

      const minDate = getMinDate(restaurant, subtotalCents);
      if (date < minDate) return false;

      if (restaurant?.cateringWindow) {
        const today = moment().startOf('day');
        const mDate = moment(date).startOf('day');
        const result = computeEffectiveStartsInDays(restaurant, today.toDate(), subtotalCents);
        if (!result) return false;

        const daysFromToday = mDate.diff(today, 'days');
        return daysFromToday >= result.startsInDays && daysFromToday <= getCateringMaxDaysFromToday(result, orderType);
      }

      return true;
    },
    [getMinDate]
  );

  // Validate a date string (YYYY-MM-DD format)
  const validateDateString = useCallback(
    (dateString, restaurant, subtotalCents, orderType) => {
      if (!dateString) return false;
      const date = moment(dateString).toDate();
      return validateDate(date, restaurant, subtotalCents, orderType);
    },
    [validateDate]
  );

  return {
    getMinDate,
    validateDate,
    validateDateString,
  };
};
